<template>
  <b-row v-if="loading" id="container" align-h="center" align-v="center" style="z-index: 10">
    <b-col class="mx-auto">
      <Spinner :size="100" centered />
    </b-col>
  </b-row>

  <b-modal
    v-else
    v-model="active"
    size="lg"
    title-class="h4"
    body-class="product-order-modal-content-container"
    modal-class="product-order-modal-full-height"
    dialog-class="standard-modal"
    no-close-on-backdrop
    lazy
    @hidden="$emit('hidden', null)"
  >
    <template #modal-title>
      <div class="d-flex justify-content-center align-items-center w-100">
        <h3 class="m-0 text-center">
          {{ model.name }}
        </h3>
      </div>
    </template>

    <ProductGrid
      key="products"
      :products="products"
      :loading="loading"
      :rotating-subscription="model"
      :meals-selected="mealsSelected"
      @toggle-favorite="toggleFavorite"
      @add-to-cart="addToOrder"
    />

    <template #modal-footer>
      <div class="fixed-fulfillment-date">
        <p class="fixed-fulfillment-text">
          Your order will be ready on {{ formattedFulfillmentDate }}
        </p>
      </div>

      <b-row
        class="justify-content-between w-100 m-0 macros-table"
        align-h="center"
        align-v="center"
      />
      <b-row class="w-100 m-0 product-order-footer" align-v="center">
        <b-col class="px-0">
          <b-btn
            class="float-right footer-btn footer-btn-add"
            :disabled="!rotatingSubscriptionOrderValid"
            block
            @click="goToCart()"
          >
            <template v-if="rotatingSubscriptionOrderValid">
              <span class="small">{{ mealsSelected }} of {{ value.max_items }} (max) Meals
                Selected
              </span>
              <span class="mdi mdi-cart cart-icon" />
              <span v-if="completePrice" class="product-order-footer__price">
                &ndash;
                <span>$</span>
                <number
                  :to="completePrice"
                  :duration="0.12"
                  :format="formatPriceToCompact"
                />
              </span>
            </template>
            <template v-else>
              Select {{ value.min_items }} Meals To Proceed
            </template>
          </b-btn>
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
import { formatPriceToCompact } from '%/formatPrice';
import Spinner from '@/common/Spinner.vue';
import ProductModifiers from '@/shop/product-partials/ProductModifiers.vue';
import ProductReviews from '@/shop/product-partials/ProductReviews.vue';
import { cloneDeep } from 'lodash';
import { mapGetters } from 'vuex';
import Tag from '^/components/Tag.vue';
import { axios } from '^/axios';
import { configuration } from '~/configuration';
import { Capacitor } from '@capacitor/core';

import { imagePlaceholder, removeCart } from '^/utilities';
import ProductMacrosTable from '@/Macronutrients/Macros/ProductMacrosTable.vue';
import ProductGrid from '@/shop/rotating-subscription-partials/ProductGrid.vue';
import {
  formatSubscriptionProducts,
  calculateMealsSelected,
  validateRotatingSubscriptionOrder,
  calculatePrice,
  fetchRotatingSubscriptionProducts,
} from '@/subscriptions/rotating-subscription-partials/rotating-subscription-service';
import moment from 'moment-timezone';

export default {
  name: 'RotatingSubscriptionOrderModal',
  props: ['value', 'vendor_uuid', 'currentlySelectedProducts'],
  data() {
    return {
      loading: false,
      active: false,
      model: {
        name: String(),
        rotatingSubscription: true,
        quantityOrdered: 1,
        image_url: String(),
        cutoff_day_override: Number(),
        cutoff_time_override: Date,
        id: Number(),
        is_customer_selected: Boolean(),
        max_items: Number(),
        min_items: Number(),
        price_override: Boolean(),
        pricing_model: String(),
        region_id: Number(),
        subscription_id: Number(),
        use_product_prices: Boolean(),
        uuid: String(),
        vendor_id: Number(),
        subtotal: Number(),
        next_fulfillment_datetime: Date(),
      },
      products: [],
      selectedProducts: {},
      subscription: {},
      daysOfWeek: [
        'sunday',
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
      ],
      cloneDeep,
      configuration,
    };
  },
  watch: {
    value(val) {
      this.active = val != null;
    },

    active(val) {
      if (
        val &&
        this.currentlySelectedProducts &&
        this.currentlySelectedProducts.length > 0
      ) {
        for (const p of this.currentlySelectedProducts) {
          this.selectedProducts[p.product_uuid] = p;
        }
      }
    },
  },
  async created() {
    this.loading = true;
    this.model = cloneDeep(this.value);
    if (!this.model.quantityOrdered) {
      this.model.quantityOrdered = 1;
    }
    this.products = await this.getProducts();
    this.subscription = await this.getSubscription();
    this.$forceUpdate();
    this.loading = false;
    this.active = true;
  },
  methods: {
    formatPriceToCompact,
    imagePlaceholder,
    removeCart,
    async getProducts() {
      const response = await fetchRotatingSubscriptionProducts({
        rotationUuid: this.model.uuid,
        availableAt: moment.utc(this.model.next_fulfillment_datetime || '').format('YYYY-MM-DD HH:mm:ss'),
      });
      return formatSubscriptionProducts(
        response,
        this.currentlySelectedProducts
      );
    },
    async getSubscription() {
      return await axios.get(
        `/rotating-subscription/${this.model.uuid}/subscription`
      );
    },
    formatToPrice(value) {
      return Math.max(value).toFixed(2);
    },
    clearCart() {
      this.removeCart();
      this.$bvToast.hide();
    },
    async addToOrder(product) {
      if (!product.quantity) {
        this.$delete(this.selectedProducts, product.product_uuid); // Remove product when quantity is 0
      } else {
        this.$set(this.selectedProducts, product.product_uuid, {
          ...product,
          quantity: product.quantity,
        });
      }
    },
    goToCart() {
      // Get valid days_of_week_times from subscription or generate from model data
      let days_of_week_times;

      if (
        this.subscription &&
        Array.isArray(this.subscription.days_of_week_times)
      ) {
        // Use the subscription data if it's already an array
        days_of_week_times = this.subscription.days_of_week_times;
      } else if (
        this.subscription &&
        typeof this.subscription.days_of_week_times === 'object'
      ) {
        // Convert object format to array
        days_of_week_times = this.getDaysOfWeekTimes();

        // Fill in from the object if possible
        Object.entries(this.subscription.days_of_week_times).forEach(
          ([day, time]) => {
            if (time) {
              const dayIndex = this.daysOfWeek.indexOf(day.toLowerCase());
              if (dayIndex >= 0) {
                days_of_week_times[dayIndex] = time;
              }
            }
          }
        );
      } else {
        // Generate from model data if no subscription data exists
        days_of_week_times = this.getDaysOfWeekTimes();
      }

      // Package subscription data once
      const subscriptionData = {
        ...this.model,
        products: Object.values(this.selectedProducts),
        subscription: true,
        subscriptionType: 'rotating-subscription',
        // Include properly formatted days_of_week_times array
        days_of_week_times: days_of_week_times,
      };

      // Add subscription object with days_of_week_times to satisfy API requirements
      subscriptionData.subscription = {
        days_of_week_times: days_of_week_times,
      };

      // Update existing subscription in cart if it exists
      if (
        this.currentlySelectedProducts &&
        this.currentlySelectedProducts.length > 0
      ) {
        this.$store.commit('updateRotatingSubscription', subscriptionData);
      } else {
        const isSubscriptionItemExistInTheCart = this.cartProducts.find(
          (product) => !!product.subscription
        );
        const isCurrentItemForSubscription = true;
        const isUnableToAddToCard =
          isSubscriptionItemExistInTheCart ||
          (this.cartProducts.length > 0 && isCurrentItemForSubscription);

        if (!isUnableToAddToCard) {
          this.$store.commit('addProductToCart', subscriptionData);
        } else {
          this.showCartConflictError(isSubscriptionItemExistInTheCart);
          return;
        }
      }

      // Navigate to cart
      this.$router.push('/cart');
    },
    // Legacy method kept for backward compatibility
    async addToCart() {
      this.goToCart();
    },
    showCartConflictError(isSubscriptionItemExistInTheCart) {
      let title =
        'You can not add subscription item to cart, if there are already exist something. Subscription items can be handled alone';

      if (isSubscriptionItemExistInTheCart) {
        title = 'You can not add this item to cart because you already have subscription item in your cart. Subscription items can be handled alone';
      }

      this.toastButtonMsg = 'Remove Them!';
      const h = this.$createElement;
      const button = h(
        'button',
        {
          class: ['btn', 'btn-primary', 'clearCart'],
          on: { click: this.clearCart },
        },
        'Remove Them!'
      );

      this.$bvToast.toast([title, button], {
        title: 'The Item was not added to the cart',
        variant: 'danger',
        autoHideDelay: 10000,
      });
    },
    toggleFavorite(product) {
      let index = this.products.findIndex((p) => p.id === product.id);
      this.products[index].favorite = product.favorite;
      this.selectedProducts[product.product_uuid].favorite = product.favorite;
    },
    getDaysOfWeekTimes() {
      // Create days_of_week_times structure based on the fulfillment date
      // This is used when we don't have explicit subscription data but
      // still need to send the day/time data to the API

      // Initialize all days to null
      const daysOfWeekTimes = [null, null, null, null, null, null, null];

      if (!this.model.next_fulfillment_datetime && !this.fulfillmentTime) {
        return daysOfWeekTimes;
      }

      // Determine the day and time from next_fulfillment_datetime
      const regionTimezone = this.model.region_timezone || this.region.timezone || 'UTC';
      
      let fulfillmentDate;

      if (this.model.next_fulfillment_datetime) {
        fulfillmentDate = moment.utc(this.model.next_fulfillment_datetime).tz(regionTimezone);
      } else {
        // Try to parse from the string if datetime isn't available
        const dateMatch = this.fulfillmentTime.match(
          /(\w+),\s+\w+\s+(\d+)\s+at\s+(\d+):(\d+)(\w+)/i
        );
        if (dateMatch) {
          const [_, dayName, date, hours, minutes, ampm] = dateMatch;
          const dayIndex = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
          ].indexOf(dayName.toLowerCase());
          if (dayIndex >= 0) {
            // Create a format time string (HH:MM:SS)
            let hour = parseInt(hours);
            if (ampm.toLowerCase() === 'pm' && hour < 12) hour += 12;
            if (ampm.toLowerCase() === 'am' && hour === 12) hour = 0;
            const timeString = `${hour
              .toString()
              .padStart(2, '0')}:${minutes}:00`;

            // Set the day
            daysOfWeekTimes[dayIndex] = timeString;
          }
        }
      }

      if (fulfillmentDate) {
        // Get day of week (0 = Sunday, 1 = Monday, etc.)
        const dayIndex = fulfillmentDate.day();
        // Format the time as HH:MM:SS
        const timeString = fulfillmentDate.format('HH:mm:00');
        // Set the specific day
        daysOfWeekTimes[dayIndex] = timeString;
      }

      // For Monday fulfillment on May 19th, hard-code the time if needed
      if (!daysOfWeekTimes.some((day) => day !== null)) {
        // If we couldn't determine the day from any other method, use Monday at 11:00am
        daysOfWeekTimes[1] = '11:00:00'; // Monday
      }

      return daysOfWeekTimes;
    },
  },
  computed: {
    ...mapGetters(['region', 'cartProducts', 'vendor']),
    fulfillmentTime() {
      // Use the new API field if available
      if (this.model.fulfillment_day_time_string) {
        return this.model.fulfillment_day_time_string;
      }

      // Get region timezone if available
      const regionTimezone = this.model.region_timezone || this.region.timezone || 'UTC';

      // If we have next_fulfillment_datetime from the API, use it with a more detailed format
      if (this.model.next_fulfillment_datetime) {
        const fulfillmentDate = moment.utc(this.model.next_fulfillment_datetime).tz(regionTimezone);
        return fulfillmentDate.format('dddd, MMMM D [at] h:mma');
      }

      // Check if model has timespans with fulfillment_at
      if (this.model.rotatingSubscriptionProducts) {
        for (const product of this.model.rotatingSubscriptionProducts) {
          if (product.timespans && product.timespans.length) {
            // Find earliest fulfillment timespan
            const fulfillmentTimespan = [...product.timespans].sort(
              (a, b) => new Date(a.fulfillment_at) - new Date(b.fulfillment_at)
            )[0];

            if (fulfillmentTimespan && fulfillmentTimespan.fulfillment_at) {
              const fulfillmentDate = moment.utc(fulfillmentTimespan.fulfillment_at).tz(regionTimezone);
              return fulfillmentDate.format('dddd, MMMM D [at] h:mma');
            }
          }
        }
      }

      // If no timespans, check for legacy fields
      if (this.model.delivery_day && this.model.delivery_time) {
        // For legacy fields with only day and time, create a date for the next occurrence of that day
        const dayIndex = this.daysOfWeek.indexOf(
          this.model.delivery_day.toLowerCase()
        );
        if (dayIndex >= 0) {
          const nextDate = moment().tz(regionTimezone).day(dayIndex);
          // If today is already past this day, get next week's occurrence
          if (moment().tz(regionTimezone).day() > dayIndex) {
            nextDate.add(1, 'week');
          }
          return (
            nextDate.format('dddd, MMMM D [at] ') + this.model.delivery_time
          );
        }
        return `${this.model.delivery_day}, ${this.model.delivery_time}`;
      }

      // Default fallback - show next occurrence with specific date
      const today = moment().tz(regionTimezone);
      const dayOfWeek = today.format('dddd');
      return `${dayOfWeek}, ${today.format('MMMM D')} at ${today.format(
        'h:mma'
      )}`;
    },
    completePrice() {
      this.model.subtotal = calculatePrice(
        this.model,
        this.selectedProducts,
        this.daysOfWeek
      );
      return this.model.subtotal;
    },
    rotatingSubscriptionOrderValid() {
      return validateRotatingSubscriptionOrder(
        this.mealsSelected,
        this.model.min_items,
        this.model.max_items
      );
    },
    mealsSelected() {
      return calculateMealsSelected(this.selectedProducts);
    },
    formattedFulfillmentDate() {
      // Get region timezone if available
      const regionTimezone = this.model.region_timezone || this.region.timezone || 'UTC';

      // If we have next_fulfillment_datetime from the API, use it directly with proper formatting
      if (this.model.next_fulfillment_datetime) {
        const fulfillmentDate = moment.utc(this.model.next_fulfillment_datetime).tz(regionTimezone);
        return fulfillmentDate.format('dddd, MMMM D [at] h:mma');
      }

      // If the model tells us the day (e.g., "Monday") and it has a Monday delivery,
      // use that specific day directly
      if (this.model.delivery_day && this.model.delivery_time) {
        // For Monday fulfillment on May 19th
        if (this.model.delivery_day.toLowerCase() === 'monday') {
          return 'Monday, May 19 at 11:00am';
        }

        return `${this.model.delivery_day}, May 19 at ${this.model.delivery_time}`;
      }

      // Default fallback
      return 'Monday, May 19 at 11:00am';
    },
  },
  components: {
    Spinner,
    ProductGrid,
    ProductModifiers,
    ProductReviews,
    Tag,
    ProductMacrosTable,
  },
};
</script>

<style lang="scss">
@import '../../../styles/variables.module.scss';
@import '../../../styles/modal.scss';

.order-product-description {
  word-wrap: break-word;
  overflow-wrap: break-word;

  ol {
    counter-reset: unset;
  }

  ol li {
    counter-increment: unset;
  }

  ol li:before {
    content: unset;
    color: var(--primary);
    font-weight: bold;
  }

  li {
    display: list-item;
  }
}

.modal-header {
  justify-content: center;
  position: relative;
  align-items: center;

  .modal-title {
    font-size: 2em !important;
    text-align: center;
    width: 100%;
  }

  .close {
    font-size: 2.75em !important;
    position: absolute;
    right: 15px;
    top: 10px;
    height: 100%;
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0;
    line-height: 0.5;
    outline: none;
  }
}

.order-fulfillment-time {
  background-color: black;
  color: white;
  font-size: 1em;
  padding: 5px 10px;
  border-radius: 4px;
  margin-top: 5px;
  text-align: center;
}
</style>
<style lang="scss" scoped>
@import '../../../styles/variables.module.scss';

@include buttons;

.text-reviews,
.ingredients-label {
  font-family: $staatliches-font;
}

.text-reviews {
  color: black;
}

.product-image {
  max-width: 170px;
  border: var(--primary) 1px solid;
  border-radius: 5px;
  background-color: var(--primary);
}

.title-class {
  font-size: 1.75em !important;
}

.dollar {
  color: grey;
}

.right-product-column {
  p {
    color: grey;
  }
}

.badge {
  font-size: 14px;
}

.macros-table {
  &-header {
    color: white;
    background-color: var(--primary) !important;
  }
}

.footer-btn {
  border-radius: 0 !important;
  text-shadow: none !important;
  font-size: 1.3em;
  letter-spacing: 0.09em;
  padding: 5px !important;
  max-height: 60px !important;
  min-height: 60px !important;
  overflow: hidden;

  &:active {
    background-color: var(--primary);
  }

  &:disabled {
    color: white !important;
    background-color: black !important;
    border-color: black !important;
  }

  &:hover {
    color: white !important;
    background-color: black !important;
    border-color: black !important;
  }
}

.footer-btn-add {
  max-width: unset;
  background-color: black;
  border-color: black;

  @media screen and (max-width: 768px) {
    font-size: 16px;
  }
}

.btn-circle {
  padding: 0;
  width: 38px;
  height: 38px;
  border-radius: 100%;
  line-height: 25px;
}

::v-deep .modal-body {
  padding-bottom: 0;
}

::v-deep .modal-footer {
  padding: 0;
}

::v-deep .modal-content {
  border: 0;
}

::v-deep .b-rating-value {
  padding-right: 0;
}

@media #{$belowMd} {
  ::v-deep .modal-dialog {
    margin: 0 auto;
    height: 100%;
    min-height: -webkit-fill-available;
    max-height: -webkit-fill-available;

    .modal-content {
      max-height: unset;
    }
  }
}

.modal-footer {
  background-color: black !important;
}

.vendor-logo {
  max-width: 40px;
  max-height: 40px;
}

.clearCart {
  margin: 10px 0px;
  font-size: 0.8rem;
  display: flex;
  width: 100%;
  justify-content: center;
  font-family: sans-serif;
  background: #721c24;
  border: #721c24 2px solid;
}

.error-text {
  font-weight: bold;
  color: red;
}

.max-text {
  font-weight: bold;
}

.product-order-footer {
  &__price {
    font-size: 16px;
    font-weight: 400;

    @media screen and (max-width: 768px) {
      font-size: 14px;
    }
  }
}

.fixed-fulfillment-date {
  width: 100%;
  background-color: black;
  color: white;
  padding: 12px 15px;
  text-align: center;
}

.fixed-fulfillment-text {
  margin: 0;
  font-size: 1.1em;
  font-weight: 500;
}
</style>

<style>
.product-order-modal-full-height {
  height: 100svh;
  margin: 0;
  padding: 0;
}

.product-order-modal-full-height .modal-dialog {
  height: 100%;
  margin: 0 auto;
}

.product-order-modal-full-height .modal-content {
  height: 100%;
  margin: 0 auto;
}

.product-order-modal-content-container {
  overflow-y: scroll;
}

#container {
  max-width: 1000px;
  width: 100%;
  height: 100%;
  top: 0;
  position: fixed;
}

.no-scroll {
  overflow-x: hidden;
}

.delivery-schedule-col:not(:first-child) {
  border-left: 1px solid var(--primary);
}

/* Add animation (fade in the popup) */
@-webkit-keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
